import { useCallback, useEffect, useRef, useState } from "react";
import { FileText, Upload, Trash2, Loader2, RefreshCw, CheckCircle2, AlertCircle, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "@/hooks/use-toast";
import { authFetch } from "@/lib/auth";
import { documentsApi } from "@/lib/api";

interface UpstreamDoc {
  id: number;
  filename: string;
  status?: string;
  size?: number;
  created_at?: string;
}

interface LocalDoc {
  external_doc_id: number;
  filename: string;
  status: string;
  size_bytes?: number | null;
  created_at: string;
}

interface Props {
  projectId: number;
  projectName: string;
}

const formatSize = (b?: number | null) => {
  if (!b) return "—";
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  return `${(b / 1024 / 1024).toFixed(2)} MB`;
};

const statusBadge = (s?: string) => {
  if (s === "ready" || s === "completed") return <Badge variant="default" className="gap-1"><CheckCircle2 className="h-3 w-3" />Gotowy</Badge>;
  if (s === "error" || s === "failed") return <Badge variant="destructive" className="gap-1"><AlertCircle className="h-3 w-3" />Błąd</Badge>;
  return <Badge variant="secondary" className="gap-1"><Clock className="h-3 w-3" />W trakcie</Badge>;
};

export function ProjectDocuments({ projectId, projectName }: Props) {
  const [docs, setDocs] = useState<Array<UpstreamDoc & { localStatus?: string }>>([]);
  const [loading, setLoading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [deletingId, setDeletingId] = useState<number | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchDocs = useCallback(async () => {
    if (!projectId) return;
    setLoading(true);
    try {
      const res = await authFetch(`${documentsApi()}?action=list&project_id=${projectId}`);
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Failed");
      const upstream: UpstreamDoc[] = Array.isArray(data.upstream)
        ? data.upstream
        : Array.isArray(data.upstream?.documents)
        ? data.upstream.documents
        : [];
      const local: LocalDoc[] = data.local || [];
      const merged = upstream.map((u) => ({
        ...u,
        localStatus: local.find((l) => l.external_doc_id === u.id)?.status,
      }));
      setDocs(merged);
    } catch (e) {
      console.error("[documents] list error", e);
      setDocs([]);
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchDocs(); }, [fetchDocs]);

  // Poll pending docs
  useEffect(() => {
    const hasPending = docs.some((d) => {
      const s = d.localStatus || d.status;
      return s !== "ready" && s !== "completed" && s !== "error" && s !== "failed";
    });
    if (!hasPending) return;
    const t = setInterval(fetchDocs, 5000);
    return () => clearInterval(t);
  }, [docs, fetchDocs]);

  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file, file.name);
      fd.append("project_id", String(projectId));
      const res = await authFetch(`${documentsApi()}?action=upload`, { method: "POST", body: fd });
      const data = await res.json();
      if (!res.ok) throw new Error(data?.error || "Upload failed");
      toast({ title: "Wgrano dokument", description: file.name });
      fetchDocs();
    } catch (e) {
      toast({ title: "Błąd uploadu", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleDelete = async (id: number) => {
    setDeletingId(id);
    try {
      const res = await authFetch(`${documentsApi()}?action=delete&document_id=${id}&project_id=${projectId}`, { method: "DELETE" });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        throw new Error(data?.error || "Delete failed");
      }
      toast({ title: "Usunięto dokument" });
      fetchDocs();
    } catch (e) {
      toast({ title: "Błąd", description: e instanceof Error ? e.message : String(e), variant: "destructive" });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="rounded-lg border border-border bg-card p-6">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2 font-semibold">
          <FileText className="h-5 w-5 text-primary" />
          Dokumenty projektu (beta) ({docs.length})
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchDocs} disabled={loading}>
            <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} />
          </Button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.md,.txt,.docx"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) handleUpload(f);
            }}
          />
          <Button size="sm" onClick={() => fileInputRef.current?.click()} disabled={uploading}>
            {uploading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Upload className="h-4 w-4 mr-2" />}
            Wgraj
          </Button>
        </div>
      </div>

      <div className="mt-4 space-y-2">
        {loading && docs.length === 0 ? (
          <div className="flex items-center justify-center py-4">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        ) : docs.length === 0 ? (
          <p className="text-sm text-muted-foreground">Brak dokumentów w projekcie „{projectName}".</p>
        ) : (
          docs.map((d) => (
            <div key={d.id} className="flex items-center justify-between rounded-lg border border-border bg-background p-3">
              <div className="flex items-center gap-3 min-w-0">
                <FileText className="h-5 w-5 text-primary flex-shrink-0" />
                <div className="min-w-0">
                  <p className="text-sm font-medium truncate">{d.filename}</p>
                  <p className="text-xs text-muted-foreground">
                    {formatSize(d.size)}{d.created_at ? ` • ${new Date(d.created_at).toLocaleDateString()}` : ""}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {statusBadge(d.localStatus || d.status)}
                <Button
                  variant="ghost"
                  size="icon"
                  className="bg-destructive/10 hover:bg-destructive/20"
                  onClick={() => handleDelete(d.id)}
                  disabled={deletingId === d.id}
                >
                  {deletingId === d.id ? (
                    <Loader2 className="h-4 w-4 animate-spin text-destructive" />
                  ) : (
                    <Trash2 className="h-4 w-4 text-destructive" />
                  )}
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
